import React, { useEffect, useId, useState } from 'react';
import { formatINR } from './formatINR';

export function CheckoutPaymentMethod({ razorpayEnabled, value, onChange, disabled }) {
  if (!razorpayEnabled) return null;
  return (
    <fieldset className="checkout-form-section mb-0">
      <legend className="checkout-form-section__title">Payment</legend>
      <div className="store-check-list">
        <label className="form-check">
          <input
            type="radio"
            className="form-check-input"
            name="payment_method"
            value="online"
            checked={value === 'online'}
            onChange={() => onChange('online')}
            disabled={disabled}
          />
          <span className="form-check-label">Pay online now (UPI / card / netbanking)</span>
        </label>
        <label className="form-check">
          <input
            type="radio"
            className="form-check-input"
            name="payment_method"
            value="offline"
            checked={value === 'offline'}
            onChange={() => onChange('offline')}
            disabled={disabled}
          />
          <span className="form-check-label">Pay later (WhatsApp / bank transfer — we confirm your order)</span>
        </label>
      </div>
    </fieldset>
  );
}

function TicketIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 8.5V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2.5M3 8.5h18M3 8.5v7a2 2 0 0 0 2 2h2.5M21 8.5v7a2 2 0 0 1-2 2h-2.5M8 12h.01M12 12h.01M16 12h.01"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CheckoutCoupon({
  code,
  onCodeChange,
  onApply,
  onRemove,
  busy,
  applying,
  message,
  discount,
  freeShipping,
}) {
  const panelId = useId();
  const applied = discount > 0 || freeShipping;
  const hasError = Boolean(message && !applied);
  const [open, setOpen] = useState(applied || Boolean(code.trim()));

  useEffect(() => {
    if (applied) setOpen(true);
  }, [applied]);

  useEffect(() => {
    if (hasError) setOpen(true);
  }, [hasError]);

  const codeDisplay = code.trim().toUpperCase();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!busy && !applying && code.trim()) onApply();
  };

  if (applied) {
    return (
      <section className="checkout-coupon checkout-coupon--applied" aria-live="polite">
        <div className="checkout-coupon__ticket">
          <div className="checkout-coupon__ticket-perf" aria-hidden />
          <div className="checkout-coupon__ticket-body">
            <div className="checkout-coupon__ticket-top">
              <span className="checkout-coupon__ticket-icon" aria-hidden>
                <TicketIcon />
              </span>
              <div className="checkout-coupon__ticket-meta">
                <span className="checkout-coupon__ticket-label">Promo applied</span>
                <span className="checkout-coupon__ticket-code">{codeDisplay}</span>
              </div>
              <button
                type="button"
                className="checkout-coupon__change"
                onClick={onRemove}
                disabled={busy || applying}
              >
                Change
              </button>
            </div>
            {message ? <p className="checkout-coupon__ticket-note">{message}</p> : null}
            <div className="checkout-coupon__ticket-pills">
              {discount > 0 ? (
                <span className="checkout-coupon__pill checkout-coupon__pill--save">
                  −{formatINR(discount)} off
                </span>
              ) : null}
              {freeShipping ? (
                <span className="checkout-coupon__pill checkout-coupon__pill--ship">Free delivery</span>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`checkout-coupon${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="checkout-coupon__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        disabled={busy && !open}
      >
        <span className="checkout-coupon__toggle-left">
          <span className="checkout-coupon__toggle-icon" aria-hidden>
            <TicketIcon />
          </span>
          <span className="checkout-coupon__toggle-text">
            <span className="checkout-coupon__toggle-title">Have a promo code?</span>
            <span className="checkout-coupon__toggle-hint">Tap to enter your code</span>
          </span>
        </span>
        <span className="checkout-coupon__toggle-chevron" aria-hidden />
      </button>

      <div id={panelId} className="checkout-coupon__panel" hidden={!open}>
        <form className="checkout-coupon__form" onSubmit={handleSubmit}>
          <label className="checkout-coupon__field-label" htmlFor="checkout-coupon-code">
            Enter code
          </label>
          <div className={`checkout-coupon__field${hasError ? ' has-error' : ''}`}>
            <span className="checkout-coupon__prefix" aria-hidden>
              #
            </span>
            <input
              id="checkout-coupon-code"
              type="text"
              className="checkout-coupon__input"
              placeholder="RAKHI15"
              value={code}
              onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
              disabled={busy || applying}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={hasError || undefined}
              aria-describedby={hasError ? 'checkout-coupon-feedback' : undefined}
            />
            <button
              type="submit"
              className="checkout-coupon__apply"
              disabled={busy || applying || !code.trim()}
            >
              {applying ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  <span className="visually-hidden">Checking</span>
                </>
              ) : (
                'Apply'
              )}
            </button>
          </div>
        </form>
        {hasError ? (
          <p id="checkout-coupon-feedback" className="checkout-coupon__error" role="alert">
            {message}
          </p>
        ) : (
          <p className="checkout-coupon__hint">One code per order. Not case-sensitive.</p>
        )}
      </div>
    </section>
  );
}

export function CheckoutPersonalizationFields({ fields, values, onChange, disabled }) {
  if (!fields.length) return null;
  return (
    <fieldset className="checkout-form-section">
      <legend className="checkout-form-section__title">Personalization</legend>
      <p className="small text-muted">Enter the name or text to appear on personalized pieces.</p>
      {fields.map(({ line, key }) => (
        <div className="checkout-field mb-3" key={key}>
          <label className="checkout-field__label" htmlFor={`cust-${key}`}>
            {line.name}
          </label>
          <input
            id={`cust-${key}`}
            type="text"
            className="form-control checkout-input"
            maxLength={80}
            placeholder="Name or message"
            value={values[key] || ''}
            onChange={(e) => onChange(key, e.target.value)}
            disabled={disabled}
            required
          />
        </div>
      ))}
    </fieldset>
  );
}

export function CheckoutSavedAddresses({ addresses, onSelect }) {
  if (!addresses?.length) return null;
  return (
    <div className="checkout-field mb-3">
      <label className="checkout-field__label" htmlFor="saved-addr">
        Saved address
      </label>
      <select
        id="saved-addr"
        className="form-select checkout-input"
        defaultValue=""
        onChange={(e) => {
          const id = Number(e.target.value);
          const a = addresses.find((x) => x.id === id);
          if (a) onSelect(a);
        }}
      >
        <option value="">Choose a saved address…</option>
        {addresses.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label} — {a.city}, {a.pincode}
          </option>
        ))}
      </select>
    </div>
  );
}
